import { useContext, useState, useEffect } from 'react';

import { locale as localeConfig } from '@adsk/au-common-js';
import { Entity, Filter } from 'drupal-jsonapi-client';

import { configureJsonApiGlobalClient } from '../../../apis/jsonApiClient';
import { cacheTTLs } from '../../../common/constants';
import UserNamesList from '../../../components/container/user-names-list';
import UpcomingEventsItemView from '../../../components/presentational/upcoming-events/UpcomingEvent';
import UpcomingEventsView from '../../../components/presentational/upcoming-events/UpcomingEvents';
import languageOptions from '../../../helpers/languageOptions';
import { ConfigContext } from '../../contexts/ConfigContext';
import I18nContext from '../../contexts/I18nContext';
import MarketingParametersContext from '../../contexts/MarketingParametersContext';
import type { MarketingParameter } from '../../contexts/MarketingParametersContext';
import AddToCalendar from '../add-to-calendar/AddToCalendar';

interface Term {
  id: string;
  name: string;
}

interface TermData {
  id: string;
  type: string;
}

interface EventItem {
  id: string;
  title: string;
  author: React.ReactNode[];
  url: string;
  time: number;
  tags: Term[];
}

interface EventEntityData {
  entityUuid: string;
  title: string;
  field_author: string;
  field_date_time: string;
  field_link: {
    uri: string;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get: (key: string) => any;
}

function AuthorOxygen({
  values,
  userProfileURL,
  userContentURL,
}: {
  values: string[];
  userProfileURL?: string;
  userContentURL?: string;
}) {
  if (!values) {
    return null;
  }
  return (
    <UserNamesList
      oxygenIds={values.map(personId => ({ personId }))}
      userProfileURL={userProfileURL}
      userContentURL={userContentURL}
    />
  );
}

function AuthorPlainText({ values }: { values: string[] }) {
  if (!values) {
    return null;
  }
  return (
    <>
      {values.map(value => (
        <div key={value}>{value}</div>
      ))}
    </>
  );
}

function UpcomingEvents({
  baseUrl,
  langCode,
  categories,
  userProfileURL,
  userContentURL,
}: {
  baseUrl?: string;
  langCode?: string;
  categories?: Term[];
  userProfileURL?: string;
  userContentURL?: string;
}) {
  const [listItems, setListItems] = useState<EventItem[] | null>([]);
  const marketingParameters = useContext(MarketingParametersContext);
  const { D9_BASE_URL } = useContext(ConfigContext);
  const { i18n } = useContext(I18nContext);
  const parsedLangCode = langCode || i18n.language;
  const parsedBaseUrl = baseUrl || D9_BASE_URL;

  useEffect(() => {
    const fetchData = async (params: MarketingParameter[]) => {
      configureJsonApiGlobalClient({
        baseUrl: parsedBaseUrl,
        cacheMaxAge: cacheTTLs.events,
        sort: 'field_date_time',
        bypassCache: false,
      });

      const entityType = 'node';
      const entityBundle = 'event';
      const filter = [
        new Filter({
          identifier: 'field_date_time',
          path: 'field_date_time',
          operator: '>=',
          value: new Date().toISOString().split('T')[0],
        }),
        new Filter({
          identifier: 'langcode',
          path: 'langcode',
          operator: '=',
          value: parsedLangCode,
        }),
      ];

      if (categories && categories.length) {
        filter.push([
          new Filter({
            identifier: 'field_category',
            path: 'field_category.tid',
            operator: 'IN',
            value: categories.map(entry => entry.id),
          }),
        ]);
      }

      const include = ['field_terms'];
      const pageOffset = 0;
      const pageLimit = 20;

      const entities = await Entity.LoadMultiple({
        entityType,
        entityBundle,
        filter,
        include,
        pageOffset,
        pageLimit,
      });

      const list = await entities.map(async (item: EventEntityData) => {
        const result: EventItem = {
          id: item.entityUuid,
          title: item.title,
          author: [
            <AuthorOxygen
              userProfileURL={userProfileURL}
              userContentURL={userContentURL}
              values={item.field_authors}
            />,
            <AuthorPlainText values={item.field_authors_text} />,
          ],
          time: new Date(item.field_date_time).getTime() / 1000,
          url: '',
          tags: [],
        };

        const link = item.get('field_link');
        if (link) {
          result.url = params.appendToUrl(link.uri);
        }

        if (item.get('field_terms').data) {
          result.tags = await Promise.all(
            item.get('field_terms').data.map(async (termData: TermData) => {
              const term = await Entity.Load(
                'taxonomy_term',
                termData.type.split('--')[1],
                termData.id
              );
              return {
                id: term.get('drupal_internal__tid'),
                name: term.get('name'),
              };
            })
          );
        }

        return result;
      });

      return Promise.all(list);
    };

    fetchData(marketingParameters).then(list => {
      setListItems(list);
    });
  }, [baseUrl, categories, parsedLangCode, marketingParameters]);

  if (!listItems) {
    return null;
  }

  return (
    <UpcomingEventsView>
      {listItems &&
        listItems.map(item => (
          <UpcomingEventsItemView
            key={item.id}
            title={item.title}
            author={item.author}
            addToCalendar={
              <AddToCalendar
                title={item.title}
                epochStart={item.time}
                length={60}
                location={item.url}
              />
            }
            url={item.url}
            time={item.time}
            tags={item.tags}
          />
        ))}
    </UpcomingEventsView>
  );
}

// shared attribute definition for Block Editor and AURR
UpcomingEvents.attributes = {
  baseUrl: {
    type: 'string',
  },
  langCode: {
    enum: localeConfig.urlLocales,
    default: localeConfig.defaultURLLocale,
    list: languageOptions,
  },
  categories: {
    type: 'array',
    default: [],
  },
  contentWidth: {
    type: 'string',
    default: 'full',
  },
};

export default UpcomingEvents;
